import { HTMLAttributes, useRef } from 'react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import ResponsiveVideo from '@/components/base/ResponsiveVideo'
import ScrollingIcon from '@/components/base/ScrollingIcon'
import Typography from '@/components/base/Typography'
import HeroButton from '@/components/sections/HeroButton'

import { heroResize } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPVideo } from '@/types'

export type HeroProps = HTMLAttributes<HTMLElement> &
  Partial<{
    video_desktop: WPVideo
    video_mobile: WPVideo
    poster: WPImage
    title: string
    description: string
    button: WPLink
  }>

const Hero = ({
  video_desktop,
  video_mobile,
  poster,
  title,
  description,
  button,
  className
}: HeroProps) => {
  const wrapper = useRef<HTMLDivElement>(null!)

  return (
    <>
      <Motion animation={heroResize}>
        {(motionRef) => (
          <section
            ref={(ref) => {
              // @ts-ignore
              motionRef.current = ref
              // @ts-ignore
              wrapper.current = ref
            }}
            data-hero
            className={cn(['relative z-10 h-svh w-full text-white', className])}
          >
            <div
              className="relative z-10 h-full w-full overflow-hidden"
              data-hero-section
            >
              <div
                className={cn([
                  'relative z-10 h-full w-full',
                  'after:absolute after:left-0 after:top-0 after:z-20 after:h-full after:w-full after:bg-[rgba(0,0,0,0.2)]'
                ])}
                data-motion-media
              >
                {video_desktop?.url || video_mobile?.url ? (
                  <ResponsiveVideo
                    video_desktop={video_desktop}
                    video_mobile={video_mobile}
                    poster={poster}
                    className="absolute inset-0 z-10 h-full w-full object-cover"
                  />
                ) : (
                  <Image
                    source={poster}
                    size="full"
                    className={cn(['h-full w-full object-cover'])}
                    data-motion-image
                  />
                )}
              </div>
              <div className="absolute left-0 top-1/2 z-10 w-full -translate-y-1/2 mix-blend-difference">
                <Container>
                  <Grid>
                    <div className="col-span-4 text-center md:col-span-6 lg:col-span-6 lg:col-start-4">
                      <div className="flex flex-col gap-[3rem]">
                        {title ? (
                          <Typography
                            className="whitespace-nowrap uppercase"
                            variant="h3"
                            component="h1"
                            data-motion-title
                          >
                            {title}
                          </Typography>
                        ) : null}
                        {description ? (
                          <Typography variant="body" data-motion-paragraph>
                            {description}
                          </Typography>
                        ) : null}
                      </div>
                    </div>
                  </Grid>
                </Container>
              </div>
              <div className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2">
                <ScrollingIcon
                  className="lg:absolute lg:bottom-0 lg:left-0"
                  theme="dark"
                />
              </div>
            </div>
          </section>
        )}
      </Motion>
      <HeroButton className="fixed bottom-[4.5rem] z-20" />
    </>
  )
}

export default Hero
